import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f8fa;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 1.2rem;
  box-shadow: 0 2px 16px rgba(233, 125, 125, 0.09);
  padding: 2.5rem 2.2rem 2.2rem 2.2rem;
  width: 100%;
  max-width: 370px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #e97d7dff;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Label = styled.label`
  font-size: 1rem;
  color: #444;
  margin-bottom: 0.3rem;
  display: block;
`;

const Input = styled.input<{ error?: boolean }>`
  width: 100%;
  padding: 0.7rem 1rem;
  border-radius: 0.7rem;
  border: 1.5px solid ${(p) => (p.error ? "#e97d7dff" : "#e0e0e0")};
  font-size: 1rem;
  margin-bottom: 0.7rem;
  background: #fafafa;
  transition: border 0.2s;
  &:focus {
    border-color: #e97d7dff;
    outline: none;
  }
`;

const ErrorMsg = styled.div`
  color: #e97d7dff;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
`;

const Button = styled.button`
  background: #e97d7dff;
  color: #fff;
  border: none;
  border-radius: 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.9rem 2.2rem;
  cursor: pointer;
  margin-top: 0.7rem;
  margin-bottom: 0.7rem;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #fff;
    color: #e97d7dff;
    border: 2px solid #e97d7dff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.7rem;
`;

const CheckboxLabel = styled.label`
  font-size: 0.98rem;
  color: #444;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const Link = styled.a`
  color: #e97d7dff;
  font-size: 0.98rem;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #000;
  }
`;

const BottomText = styled.div`
  text-align: center;
  font-size: 1rem;
  margin-top: 1.2rem;
  color: #444;
`;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [form, setForm] = useState({
    email: "",
    password: "",
    remember: false,
  });
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const validate = () => {
    const errs: { [k: string]: string } = {};
    if (!emailRegex.test(form.email)) errs.email = "Invalid email format.";
    if (!form.password || form.password.length < 8)
      errs.password = "Password must be at least 8 characters.";
    return errs;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    setErrors((errs) => {
      const newErrs = { ...errs };
      delete newErrs[name];
      return newErrs;
    });
    setApiError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setLoading(true);
    setApiError("");
    try {
      await signIn(form.email, form.password);
      navigate("/");
    } catch (err: any) {
      setApiError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Card
        as="form"
        onSubmit={handleSubmit}
        autoComplete="off"
        aria-label="Sign In Form"
      >
        <Title>Sign In to Your Account</Title>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          error={!!errors.email}
          autoComplete="email"
          required
          aria-invalid={!!errors.email}
        />
        {errors.email && <ErrorMsg>{errors.email}</ErrorMsg>}
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          name="password"
          type="password"
          value={form.password}
          onChange={handleChange}
          error={!!errors.password}
          autoComplete="current-password"
          required
          aria-invalid={!!errors.password}
        />
        {errors.password && <ErrorMsg>{errors.password}</ErrorMsg>}
        <Row>
          <CheckboxLabel>
            <input
              type="checkbox"
              name="remember"
              checked={form.remember}
              onChange={handleChange}
            />
            Remember Me
          </CheckboxLabel>
          <Link
            as="button"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              navigate("/forgot-password");
            }}
          >
            Forgot Password?
          </Link>
        </Row>
        <Button
          type="submit"
          disabled={loading || Object.keys(validate()).length > 0}
          aria-disabled={loading || Object.keys(validate()).length > 0}
        >
          {loading ? "Signing In..." : "Sign In"}
        </Button>
        {apiError && <ErrorMsg>{apiError}</ErrorMsg>}
        <BottomText>
          Don’t have an account?{" "}
          <Link as="button" type="button" onClick={() => navigate("/signup")}>
            Create one
          </Link>
        </BottomText>
      </Card>
    </Wrapper>
  );
};

export default SignIn;
